import { formatDateTime } from '../dates';
import {
  BRAND_CSS,
  PRINT_SCRIPT,
  brandHeaderHtml,
  escapeHtml,
} from './printShared';

// One entry as a printable page (Implementation List §2 "PDF (one entry)"):
// Letter, 12mm margins, the logo header, an "Entry · ID" eyebrow with the
// title and the submission date, an orange rule, then each section's
// answers in a 3-column grid. The page opens the print dialog itself.
//
// sections: [{ label, cells: [{ label, span, kind, … }] }] where kind is
//   'text'      { text, long }   — '' prints an em dash
//   'agreement' { agreed }       — "✓ Agreed" / "Not agreed"
//   'grid'      { columns, rows } — repeaters and tables, a small table

const EMPTY = '<span class="e">—</span>';

const cellValue = (cell) => {
  if (cell.kind === 'agreement') {
    return cell.agreed
      ? '<span class="ok">&#10003; Agreed</span>'
      : '<span class="e">Not agreed</span>';
  }
  if (cell.kind === 'grid') {
    if (!cell.rows || !cell.rows.length) return EMPTY;
    const head = cell.columns.map((c) => `<th>${escapeHtml(c)}</th>`).join('');
    const body = cell.rows
      .map(
        (row) =>
          `<tr>${row
            .map((v) => `<td>${escapeHtml(v || '—')}</td>`)
            .join('')}</tr>`
      )
      .join('');
    return `<table><thead><tr>${head}</tr></thead><tbody>${body}</tbody></table>`;
  }
  if (!cell.text) return EMPTY;
  return cell.long
    ? `<span class="long">${escapeHtml(cell.text)}</span>`
    : escapeHtml(cell.text);
};

const cellHtml = (cell) =>
  `<div class="c"${
    cell.span > 1 ? ` style="grid-column:span ${cell.span}"` : ''
  }><div class="k">${escapeHtml(cell.label)}</div><div class="v">${cellValue(
    cell
  )}</div></div>`;

const sectionHtml = (section) =>
  `<div class="sec"><div class="sl">${escapeHtml(
    section.label
  )}</div><div class="grid">${section.cells
    .map(cellHtml)
    .join('')}</div></div>`;

const CSS = [
  '*{box-sizing:border-box;margin:0;padding:0}',
  'body{font:13px/1.45 system-ui,-apple-system,"Segoe UI",Roboto,sans-serif;color:#1F2A30;padding:34px 38px}',
  '.hd{display:flex;justify-content:space-between;align-items:flex-end;gap:24px;border-bottom:3px solid #FA4616;padding-bottom:16px}',
  '.ey{font-size:11px;letter-spacing:1.5px;text-transform:uppercase;color:#D63A0F;font-weight:800;margin-bottom:6px}',
  'h1{font-size:26px;letter-spacing:-.4px;line-height:1.15;color:#1F2A30}h1 span{color:#64748B;font-weight:700}',
  '.meta{text-align:right;font-size:11px;color:#64748B;line-height:1.7;white-space:nowrap}.meta b{color:#1F2A30}',
  '.sec{margin-top:20px;break-inside:avoid}',
  '.sl{font-size:10.5px;letter-spacing:1.6px;text-transform:uppercase;color:#D63A0F;font-weight:800;margin-bottom:7px}',
  '.grid{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:1px;background:#E2E8F0;border:1px solid #E2E8F0;border-radius:10px;overflow:hidden}',
  '.c{background:#fff;padding:9px 12px;min-width:0}',
  '.k{font-size:9.5px;letter-spacing:1px;text-transform:uppercase;color:#64748B;font-weight:700;margin-bottom:2px}',
  '.v{font-size:12.5px;font-weight:600;word-break:break-word}.long{white-space:pre-wrap;font-weight:500}',
  '.e{color:#94A3B8;font-weight:500}.ok{color:#16A34A}',
  'table{width:100%;border-collapse:collapse;font-size:11.5px;margin-top:2px}',
  'th{text-align:left;font-size:9px;letter-spacing:.8px;text-transform:uppercase;color:#64748B;padding:4px 8px 4px 0;border-bottom:1px solid #E2E8F0}',
  'td{padding:4px 8px 4px 0;border-bottom:1px solid #F1F5F9}',
  '.ft{margin-top:24px;font-size:10px;color:#94A3B8;text-align:center}',
  '@page{size:letter;margin:12mm}',
].join('');

// brand = brandInfo(account); entry = { reference, submitted_at }.
export const entryPrintHtml = ({
  brand,
  formName,
  entry,
  title,
  suffix,
  sections,
  now = new Date(),
}) => {
  const heading =
    escapeHtml(title) + (suffix ? ` <span>${escapeHtml(suffix)}</span>` : '');
  return (
    '<!DOCTYPE html><html><head><meta charset="utf-8">' +
    `<title>${escapeHtml(entry.reference)} — ${escapeHtml(formName)}</title>` +
    `<style>${CSS}${BRAND_CSS}</style></head><body>` +
    brandHeaderHtml(brand, formName) +
    `<div class="hd"><div><div class="ey">Entry · ${escapeHtml(
      entry.reference
    )}</div><h1>${heading}</h1></div>` +
    `<div class="meta">Submitted<br><b>${escapeHtml(
      formatDateTime(entry.submitted_at)
    )}</b></div></div>` +
    sections.map(sectionHtml).join('') +
    `<div class="ft">Generated ${escapeHtml(
      formatDateTime(now)
    )} · ticketOS · ${escapeHtml(brand.platform)} · Confidential</div>` +
    `${PRINT_SCRIPT}</body></html>`
  );
};

export default entryPrintHtml;
